import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { USERS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Shield } from "lucide-react";

const EMPTY = { email: "", name: "", role: "guardia", password: "" };

export default function Users() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get("/users").then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post("/users", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuario creado");
      closeForm();
    },
    onError: (e) => toast.error(apiError(e, "Error al crear usuario")),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/users/${id}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuario actualizado");
      closeForm();
    },
    onError: (e) => toast.error(apiError(e, "Error al actualizar")),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuario eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, name: u.name, role: u.role, password: "" });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error("Email y nombre son obligatorios");
      return;
    }
    if (editing) {
      const body = { name: form.name, role: form.role };
      if (form.password) body.password = form.password;
      updateMutation.mutate({ id: editing.id, body });
    } else {
      if (!form.password) {
        toast.error("La contraseña es obligatoria");
        return;
      }
      createMutation.mutate(form);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">Usuarios</h1>
          <p className="text-sm text-muted-foreground mt-1">Administra cuentas y roles del sistema.</p>
        </div>
        <button
          onClick={openCreate}
          data-testid={USERS.newButton}
          className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
        >
          <Plus size={18} /> Nuevo usuario
        </button>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <div className="bg-white border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nombre</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Rol</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid={USERS.list} className="divide-y divide-border">
                {users.map((u) => (
                  <tr key={u.id} data-testid={USERS.row} className="hover:bg-secondary/50">
                    <td className="px-4 py-3 font-medium text-navy">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 bg-navy/5 text-navy flex items-center justify-center text-xs font-bold">
                          {u.name?.charAt(0)?.toUpperCase() || "?"}
                        </span>
                        {u.name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${roleColor(u.role)}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${u.active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                        {u.active ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(u)}
                          data-testid={USERS.editButton}
                          className="w-8 h-8 flex items-center justify-center text-navy hover:bg-secondary"
                          aria-label="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                        {u.id !== me?.id && (
                          <button
                            onClick={() => deleteMutation.mutate(u.id)}
                            data-testid={USERS.deleteButton}
                            className="w-8 h-8 flex items-center justify-center text-critical hover:bg-critical/10"
                            aria-label="Eliminar"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-navy">
                {editing ? "Editar usuario" : "Nuevo usuario"}
              </h3>
              <button onClick={closeForm} className="p-2 hover:bg-secondary" aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <Field label="Nombre *">
                <input
                  data-testid={USERS.formName}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="input"
                  required
                />
              </Field>
              <Field label="Email *">
                <input
                  data-testid={USERS.formEmail}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input"
                  required
                  disabled={!!editing}
                />
              </Field>
              <Field label="Rol *">
                <select
                  data-testid={USERS.formRole}
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="input"
                >
                  <option value="admin">Administrador</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="guardia">Guardia</option>
                </select>
              </Field>
              <Field label={editing ? "Nueva contraseña (dejar vacío para mantener)" : "Contraseña *"}>
                <input
                  data-testid={USERS.formPassword}
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input"
                  required={!editing}
                />
              </Field>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid={USERS.formSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="h-11 px-5 bg-navy text-white font-semibold hover:bg-navy-hover disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? "Guardando…" : "Guardar"}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  data-testid={USERS.formCancel}
                  className="h-11 px-5 border border-border text-navy hover:bg-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function roleColor(role) {
  switch (role) {
    case "admin": return "bg-navy text-white";
    case "supervisor": return "bg-warn/20 text-warn";
    case "guardia": return "bg-secondary text-muted-foreground";
    default: return "bg-secondary text-muted-foreground";
  }
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-navy">{label}</label>
      {children}
    </div>
  );
}
